import logger from './logger.js';
import csrfManager from './csrfManager.js';

const LOCAL_API_FALLBACK_URL = 'http://localhost:3050';
const PRODUCTION_API_URL = 'https://api.launcher-app.com';
const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '0.0.0.0'];
const debugEnabled =
    (typeof import.meta !== 'undefined' && import.meta?.env?.VITE_API_DEBUG === 'true') ||
    (typeof window !== 'undefined' && Boolean(window.LAUNCHER_API_DEBUG));

/**
 * Detect if running in Electron (file:// protocol or electron protocol)
 */
function isElectronFileProtocol() {
    if (!isBrowser()) return false;
    const protocol = (window.location.protocol || '').toLowerCase();
    return protocol === 'file:' || protocol === 'app:';
}

/**
 * Check if a URL is a valid HTTP(S) base URL
 */
function isValidHttpBaseUrl(url) {
    if (!url || typeof url !== 'string') return false;
    return url.startsWith('http://') || url.startsWith('https://');
}

const log = logger.withContext('ApiClient');

// Always log important API events for debugging
function logAlways(...args) {
    log.info(...args);
}

function debug(...args) {
    if (debugEnabled) {
        log.debug(...args);
    }
}

function isBrowser() {
    return typeof window !== 'undefined' && typeof window.location !== 'undefined';
}

function isLocalHostname(hostname) {
    if (!hostname) {
        return false;
    }
    return LOCAL_HOSTNAMES.includes(hostname) || hostname.endsWith('.local');
}

function normalizeBaseUrl(value) {
    if (!value || typeof value !== 'string') {
        return '';
    }
    return value.trim().replace(/\/+$/, '');
}

function getRuntimeConfiguredUrl() {
    if (!isBrowser()) {
        return '';
    }
    return normalizeBaseUrl(window.LAUNCHER_API_URL || window.__LAUNCHER_API_URL__ || '');
}

function getEnvConfiguredUrl() {
    const envUrl = import.meta?.env?.VITE_API_URL || '';
    return normalizeBaseUrl(envUrl);
}

function deriveDefaultBaseUrl() {
    logAlways('Deriving API base URL...', {
        isBrowser: isBrowser(),
        hostname: isBrowser() ? window.location.hostname : 'N/A',
        origin: isBrowser() ? window.location.origin : 'N/A',
        protocol: isBrowser() ? window.location.protocol : 'N/A',
        isElectronFile: isElectronFileProtocol(),
    });

    const runtimeUrl = getRuntimeConfiguredUrl();
    if (runtimeUrl) {
        logAlways('Using runtime API URL from window', runtimeUrl);
        return runtimeUrl;
    }

    const envUrl = getEnvConfiguredUrl();
    if (envUrl) {
        logAlways('Using API URL from VITE_API_URL', envUrl);
        return envUrl;
    }

    // If running in Electron with file:// protocol (production build),
    // use the production API URL
    if (isElectronFileProtocol()) {
        logAlways('Electron file:// protocol detected, using production API URL', PRODUCTION_API_URL);
        return PRODUCTION_API_URL;
    }

    if (isBrowser()) {
        if (isLocalHostname(window.location.hostname)) {
            // En local, utiliser des chemins relatifs pour que le proxy Vite fonctionne
            // Le proxy redirigera /api/* vers l'API staging configurée dans vite.config.mjs
            logAlways('Local hostname detected, using relative paths for Vite proxy', '');
            return ''; // Chemins relatifs = le proxy Vite s'en occupe
        }
        // Only use window.location.origin if it's a valid HTTP(S) origin
        // Never use file:// or other non-HTTP origins as API base
        const origin = window.location.origin;
        if (origin && 
            origin !== 'null' && 
            (origin.startsWith('http://') || origin.startsWith('https://'))) {
            // Ne pas ajouter /api car tous les paths dans le code commencent déjà par /api/
            const derivedUrl = origin.replace(/\/$/, '');
            logAlways('Using API URL derived from window.location', derivedUrl);
            return derivedUrl;
        }
    }

    logAlways('Falling back to relative paths', '');
    return ''; // Chemins relatifs par défaut
}

let apiBaseUrl = deriveDefaultBaseUrl();

// Safety check: ensure the derived URL is valid HTTP(S) or empty (for proxy)
// This catches edge cases where detection logic might fail
if (apiBaseUrl !== '' && !isValidHttpBaseUrl(apiBaseUrl)) {
    logAlways('Derived API URL is not valid HTTP(S), forcing production URL', {
        derivedUrl: apiBaseUrl,
        productionUrl: PRODUCTION_API_URL
    });
    apiBaseUrl = PRODUCTION_API_URL;
}

logAlways('Initial API base URL resolved', apiBaseUrl, {
    hostname: isBrowser() ? window.location.hostname : 'server',
    envUrl: getEnvConfiguredUrl(),
});
const runningOnLocalHost = isBrowser() && isLocalHostname(window.location.hostname);
logAlways('Running on localhost:', runningOnLocalHost);

function buildAbsoluteUrl(path, base = apiBaseUrl) {
    if (!path) {
        return base;
    }
    if (path.startsWith('http')) {
        return path;
    }
    
    // Si base est vide, on utilise des chemins relatifs (pour le proxy Vite)
    if (!base || base === '') {
        const sanitizedPath = path.startsWith('/') ? path : `/${path}`;
        return sanitizedPath;
    }
    
    // Safety: ensure base URL is valid HTTP(S), fallback to production URL
    let safeBase = base;
    if (!isValidHttpBaseUrl(base)) {
        log.warn('Invalid base URL detected, falling back to production URL', { base, path });
        safeBase = PRODUCTION_API_URL;
    }
    
    const sanitizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${safeBase}${sanitizedPath}`;
}

export async function apiFetch(path, options = {}, { allowLocalFallback = true } = {}) {
    debug('apiFetch called', { path, method: options.method || 'GET', allowLocalFallback });

    if (!path) {
        const error = new Error('apiFetch requires a path to be provided');
        log.error('apiFetch missing path', error);
        throw error;
    }

    // Préparer les headers en incluant le token CSRF si nécessaire
    const method = options.method || 'GET';
    const headers = new Headers(options.headers || {});
    
    // Ajouter le token CSRF pour les requêtes qui le nécessitent
    if (csrfManager.requiresToken(method)) {
        try {
            debug('Fetching CSRF token for request', { method, path });
            const csrfToken = await csrfManager.getToken(apiBaseUrl);
            headers.set('x-csrf-token', csrfToken);
            debug('CSRF token added to request');
        } catch (error) {
            log.error('Failed to get CSRF token', error);
            // On continue quand même, le serveur renverra une erreur 403
        }
    }

    // Toujours inclure les credentials pour que les cookies fonctionnent
    const fetchOptions = {
        ...options,
        headers,
        credentials: options.credentials || 'include',
    };

    if (path.startsWith('http')) {
        debug('apiFetch → direct URL call', path);
        try {
            return await fetch(path, fetchOptions);
        } catch (error) {
            log.error('Direct fetch failed', { url: path, error });
            throw error;
        }
    }

    const targets = [apiBaseUrl];
    if (allowLocalFallback && runningOnLocalHost && apiBaseUrl !== LOCAL_API_FALLBACK_URL) {
        targets.push(LOCAL_API_FALLBACK_URL);
    }

    debug('apiFetch → targets', targets);

    let lastError;
    for (const base of targets) {
        const url = buildAbsoluteUrl(path, base);
        try {
            debug('apiFetch → calling', url);
            const response = await fetch(url, fetchOptions);
            debug('apiFetch → response', { url, status: response.status, ok: response.ok });

            // Log les erreurs HTTP importantes
            if (!response.ok && response.status >= 500) {
                log.error('Server error response', { url, status: response.status, statusText: response.statusText });
            } else if (!response.ok && response.status >= 400) {
                log.warn('Client error response', { url, status: response.status, statusText: response.statusText });
                
                // Si c'est une erreur CSRF (403), invalider le token pour le renouveler
                if (response.status === 403) {
                    const text = await response.clone().text();
                    if (text.includes('csrf') || text.includes('CSRF')) {
                        log.warn('CSRF error detected, invalidating token');
                        csrfManager.invalidate();
                    }
                }
            }

            if (base !== apiBaseUrl) {
                apiBaseUrl = base;
                log.info('Base URL switched', { newBaseUrl: apiBaseUrl });
            }
            return response;
        } catch (error) {
            lastError = error;
            const isNetworkError = error && error.name === 'TypeError';
            log.warn('apiFetch error', { url, error: error?.message || error, isNetworkError });

            if (!isNetworkError || base === targets[targets.length - 1]) {
                log.error('apiFetch giving up', { url, error: error?.message || error, stack: error?.stack });
                throw error;
            }

            log.warn(`Unable to reach ${url}, trying fallback...`, { error: error.message });
        }
    }

    log.error('All API targets failed', { targets, lastError: lastError?.message });
    throw lastError;
}

/**
 * Environments configuration
 */
export const API_ENVIRONMENTS = {
    AUTO: { label: 'Auto', value: 'auto' },
    LOCAL: { label: 'Local (localhost:3050)', value: 'local', url: LOCAL_API_FALLBACK_URL },
    STAGING: { label: 'Staging', value: 'staging', url: 'https://api.staging.launcher-app.com' },
    PRODUCTION: { label: 'Production', value: 'production', url: PRODUCTION_API_URL },
};

/**
 * Set the API environment manually
 */
export function setApiEnvironment(environment) {
    const env = API_ENVIRONMENTS[environment.toUpperCase()];
    
    if (!env) {
        log.error('Invalid environment', { environment });
        return;
    }

    // Garde-fou : sur localhost, on refuse de pointer directement sur la prod
    // (pas de proxy Vite vers la prod, et risque de manipuler des données réelles)
    if (runningOnLocalHost && environment === 'production') {
        log.warn('Production environment is not allowed from localhost — falling back to auto', {
            hostname: isBrowser() ? window.location.hostname : 'server',
        });
        return setApiEnvironment('auto');
    }

    if (environment === 'auto') {
        // Reset to automatic detection
        apiBaseUrl = deriveDefaultBaseUrl();
        localStorage.removeItem('launcher_admin_api_env');
        logAlways('API environment reset to auto', apiBaseUrl);
    } else {
        // Set specific environment
        // En local, pour staging, utiliser des chemins relatifs pour le proxy Vite
        if (runningOnLocalHost && environment === 'staging') {
            apiBaseUrl = ''; // Chemins relatifs = le proxy Vite redirige vers staging
            logAlways('Local + staging: using Vite proxy with relative paths');
        } else {
            apiBaseUrl = env.url;
        }
        localStorage.setItem('launcher_admin_api_env', environment);
        logAlways('API environment manually set', { environment, url: apiBaseUrl || 'relative (proxy)' });
    }
    
    // Invalidate CSRF token when changing environment
    csrfManager.invalidate();
    
    // Notify all components about the environment change
    if (isBrowser()) {
        window.dispatchEvent(new CustomEvent('launcher:env-changed', { 
            detail: { environment: localStorage.getItem('launcher_admin_api_env') || 'auto', url: apiBaseUrl || 'relative (proxy)' } 
        }));
    }
    
    return apiBaseUrl;
}

/**
 * Get current API environment setting
 */
export function getCurrentEnvironment() {
    const saved = localStorage.getItem('launcher_admin_api_env');
    if (!saved || !API_ENVIRONMENTS[saved.toUpperCase()]) {
        return 'auto';
    }
    // Garde-fou : sur localhost, on n'expose jamais "production" (force auto + nettoie le storage)
    if (runningOnLocalHost && saved === 'production') {
        log.warn('Saved environment "production" detected on localhost — resetting to auto');
        try {
            localStorage.removeItem('launcher_admin_api_env');
        } catch (_) {
            // ignore (storage indispo)
        }
        return 'auto';
    }
    return saved;
}

// Check if there's a saved environment preference and apply it
const savedEnv = getCurrentEnvironment();
if (savedEnv !== 'auto') {
    setApiEnvironment(savedEnv);
}
